import { useTranslation } from "@/features/utils/i18n";
import { useCallback } from "react";
import type { OAuthPopupResult } from "./hooks/use-oauth-popup";

/**
 * What to tell the user when a consent popup ends without an account.
 *
 * Every screen that connects an account reads the same outcome from
 * `useOAuthPopup`, and they each used to invent their own wording for it — one
 * announced "Account connected" for a window that connected nothing, the other
 * said nothing at all. The codes below are the hook's own; anything else is a
 * message from the provider (or from our own callback page) and is shown as it
 * is, because it is the only thing that says *why*.
 */
export function useOAuthErrorMessage() {
  const { t } = useTranslation("app");

  return useCallback(
    (result: OAuthPopupResult): string => {
      switch (result.error) {
        case "popup_blocked":
          return t(
            "externalAccounts.messages.oauthPopupBlocked",
            "The connection window was blocked by your browser. Allow popups for this site and try again.",
          );
        case "closed_before_completion":
          return t(
            "externalAccounts.messages.oauthClosed",
            "The authorization window was closed before the account was connected.",
          );
        default:
          if (result.error?.startsWith("oauth_wrong_origin:")) {
            return t("externalAccounts.messages.oauthWrongOrigin", {
              origin: result.error.slice("oauth_wrong_origin:".length),
              defaultValue:
                "The authorization came back from {{origin}}, which is not this environment: the account was created there. The provider's redirect URI points at the wrong deployment.",
            });
          }
          // Anything else is the provider's own words, or our callback's, and
          // they are the only thing that says *why*. With nothing at all to go
          // on, say what is certain — no account was connected — rather than
          // blame the launch of a flow that plainly started.
          return (
            result.error ||
            t(
              "externalAccounts.messages.oauthNotConnected",
              "The authorization finished without connecting an account.",
            )
          );
      }
    },
    [t],
  );
}
